/**
 * Box 4: Spend bars and a Clicks line on the right axis (spec §3.3), daily up
 * to 14 days and weekly above (R14). A null bucket is a gap. Styled with the
 * /looker chart theme so both pages read as one app (R12).
 *
 * @module components/overview/SpendClicksChart
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { AXIS_PROPS, CHART_HEIGHT, GRID_PROPS, LEGEND_PROPS, TOOLTIP_PROPS, categoryTick, compactTick } from '@/components/looker/charts/chartTheme';
import { colorAt } from '@/components/charts/chartPalette';
import { fmtCount, fmtMoney } from '@/services/overview/overviewFormat';
import type { Trend } from '@/services/overview/spendTrend';

export const SpendClicksChart: React.FC<{ trend: Trend; currency: string | null }> = ({ trend, currency }) => {
  const { t } = useTranslation();
  const spendName = t('overview.metric.spend');
  return (
    <div data-trend-grain={trend.grain}>
      <p className="mb-2 text-xs text-neutral-700">{trend.grain === 'day' ? t('overview.trend.daily') : t('overview.trend.weekly')}</p>
      <div style={{ height: CHART_HEIGHT }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={trend.points} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid {...GRID_PROPS} />
            <XAxis dataKey="key" {...AXIS_PROPS} tickFormatter={categoryTick(t)} />
            <YAxis yAxisId="spend" {...AXIS_PROPS} tickFormatter={compactTick} />
            <YAxis yAxisId="clicks" orientation="right" {...AXIS_PROPS} tickFormatter={compactTick} />
            <Tooltip
              {...TOOLTIP_PROPS}
              labelFormatter={categoryTick(t)}
              formatter={(v: unknown, name: unknown) => {
                const n = typeof v === 'number' ? v : null;
                return name === spendName ? fmtMoney(n, currency) : fmtCount(n);
              }}
            />
            <Legend {...LEGEND_PROPS} />
            <Bar yAxisId="spend" dataKey="spend" name={spendName} fill={colorAt(0)} radius={[4, 4, 0, 0]} />
            <Line yAxisId="clicks" dataKey="clicks" name={t('overview.metric.clicks')} stroke={colorAt(1)} strokeWidth={2} dot={false} connectNulls={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
